import { createSelector, createSlice, PayloadAction, SerializedError } from '@reduxjs/toolkit';

import { Alert, AlertId, AlgoliaAlert } from '../../../types/alerts';
import { TrailId } from '../../../types/trails';
import { getAlgoliaAlert } from '../../../utils/alertsHelper';
import { createAlert, getAlert, updateAlert } from '../../thunks/alerts';
import { clearAllFilters } from '../filters';

export enum AlertDrawerActionType {
  ADD = 'add',
  EDIT = 'edit'
}

export enum AlertDrawerActionTarget {
  TRAILS = 'trails',
  AREAS = 'areas'
}

export type AlertDrawerOpenAction = {
  visible: boolean;
  action?: AlertDrawerActionType;
  target?: AlertDrawerActionTarget;
  alerts?: AlgoliaAlert[] | null;
  alertId?: AlertId | null;
};

export type AlertsDrawerState = {
  visible: boolean;
  pending: boolean;
  saving?: boolean;
  saved?: boolean;
  error?: SerializedError;
  action?: AlertDrawerActionType;
  target?: AlertDrawerActionTarget;
  trailIds?: TrailId[];
  alerts?: AlgoliaAlert[] | null;
  alertId?: AlertId | null;
  alert?: Alert | null;
  createdAlerts?: AlgoliaAlert[] | null;
};

export const initialState: AlertsDrawerState = {
  visible: false,
  pending: false,
  saving: false,
  saved: false,
  action: null,
  target: null,
  trailIds: [],
  alerts: [],
  alertId: null,
  alert: null,
  createdAlerts: []
};

type PartialState = {
  alertsDrawer: AlertsDrawerState;
};

export const selectEditing = createSelector(
  ({ alertsDrawer }: PartialState) => alertsDrawer.visible,
  ({ alertsDrawer }: PartialState) => alertsDrawer.action,
  ({ alertsDrawer }: PartialState) => alertsDrawer.alertId,
  (visible, action, alertId) => visible && action === AlertDrawerActionType.EDIT && alertId !== null
);

export const drawerStateSlice = createSlice({
  name: 'drawerState',
  initialState,
  reducers: {
    setVisible: (state, action: PayloadAction<AlertDrawerOpenAction>) => {
      state.visible = action.payload.visible;
      state.alerts = action.payload.alerts;
      state.action = action.payload.action || null;
      state.target = action.payload.target || null;
      state.alertId = action.payload.alertId || null;
      if (state.alertId === null && state.alerts?.length === 1) {
        state.alertId = state.alerts[0].ID;
      }
      state.alert = null;
      state.createdAlerts = [];
      state.pending = false;
    },
    setTrailIds: (state, action: PayloadAction<TrailId[]>) => {
      state.trailIds = action.payload;
    },
    setPending: (state, action: PayloadAction<boolean>) => {
      state.pending = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(createAlert.pending, state => {
        state.saving = true;
        state.saved = false;
        state.error = null;
      })
      .addCase(createAlert.fulfilled, (state, action) => {
        state.pending = false;
        state.saving = false;
        state.saved = true;
        state.createdAlerts = action.payload.map(alert => getAlgoliaAlert(alert));
      })
      .addCase(createAlert.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error;
      })
      .addCase(updateAlert.pending, state => {
        state.saving = true;
        state.saved = false;
        state.error = null;
      })
      .addCase(updateAlert.fulfilled, state => {
        state.pending = false;
        state.saving = false;
        state.saved = true;
      })
      .addCase(updateAlert.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error;
      })
      .addCase(getAlert.pending, state => {
        state.alert = null;
        state.error = null;
      })
      .addCase(getAlert.fulfilled, (state, action) => {
        state.alert = action.payload;
      })
      .addCase(getAlert.rejected, (state, action) => {
        state.error = action.error;
      })
      .addCase(clearAllFilters, state => {
        state.visible = false;
        state.pending = false;
        state.action = null;
        state.target = null;
        state.alertId = null;
        state.alert = null;
      });
  }
});

export const { setVisible, setTrailIds, setPending } = drawerStateSlice.actions;

export default drawerStateSlice.reducer;
